import { Route as ReactRouterRoute, Switch, Redirect, useHistory, useLocation } from '@alltrails/shared/components/ReactRouter';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { hasFormErrors } from 'utils/GiftFlowHelper';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import { pushHistory } from 'utils/client_side_routing_helpers';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import useCountryId from 'hooks/useCountryId';
import User from '@alltrails/shared/types/User';
import { useDispatch } from 'react-redux';
import useUser from 'hooks/useUser';
import PaymentType from 'types/PaymentType';
import LoginForm from 'components/LoginForm';
import SignUpForm from 'components/SignUpForm';
import LoginSignupLayout from 'components/LoginSignupLayout';
import { PropsWithChildren, useState } from 'react';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { UPDATE_PURCHASED_GIFT_CARD, UPDATE_SERVER_ERRORS, useGiftFlowContext } from 'pages/Gift/giftContext';
import { serverAjaxPromise } from 'utils/requests/request_helpers';
import useIpCountryCode from 'hooks/useIpCountryCode';
import * as styles from './styles/styles.module.scss';
import CustomizeGiftForm from '../CustomizeGiftForm';
import GiftLandingPage from '../Landing/GiftLandingPage';
import GiftCardPrint from '../GiftCardPrint';
import GiftConfirmation from '../GiftConfirmation';
import PaymentForm from '../../forms/pro/PaymentForm';
import MiniFooter from '../MiniFooter';
import GiftReview from '../GiftReview';
import { setUser } from '../../../userSlice';

const MINI_FOOTER_PATHNAMES = ['/gift/payment'];
const INVALID_EMAIL = 'invalid_email';

type Props = {
  currencyCode: CurrencyCode;
  plan: Plan;
  shopifyProducts?: unknown;
  paymentTypes: PaymentType[];
};

function Route({ children, exact, path }: PropsWithChildren<{ exact?: boolean; path: string }>) {
  return (
    <ReactRouterRoute exact={exact} path={path}>
      {children}
    </ReactRouterRoute>
  );
}

// pathname matching with locale
const localizedRouteIncludes = (pathname: string, routes: string[] = [], languageRegionCode: LanguageRegionCode) => {
  let path;

  if (languageRegionCode === 'en-US') {
    path = pathname;
  } else {
    const prefix = wrapUrlSafe('/', languageRegionCode);
    path = pathname.replace(prefix, '/');
  }

  return routes.includes(path);
};

const GiftFlowRouting = ({ currencyCode: initialCurrencyCode, plan, paymentTypes, shopifyProducts }: Props) => {
  const dispatch = useDispatch();
  const languageRegionCode = useLanguageRegionCode();
  const countryId = useCountryId();
  const user = useUser();
  const [currencyCode, setCurrencyCode] = useState(initialCurrencyCode);
  const [billingCountry, setBillingCountry] = useState(useIpCountryCode());

  const {
    giftFlowState: { giftCustomizeParams, purchasedGiftCard },
    dispatchGiftFlow
  } = useGiftFlowContext();

  const history = useHistory();
  const location = useLocation();

  const handleAuthSuccess = ({ user }: { user: User }) => {
    dispatch(setUser(user));
    pushHistory('/gift/payment', history, languageRegionCode);
  };

  const handlePaymentSuccess = (payload: any) => {
    Object.assign(payload, { isEmail: !!payload.metadata.deliveryDate });
    dispatchGiftFlow({ type: UPDATE_PURCHASED_GIFT_CARD, payload });

    pushHistory('/gift/review', history, languageRegionCode);
  };

  const handlePaymentError = (err: any) => {
    if (!err || !err.responseJSON) {
      return err;
    }

    const { errors } = err.responseJSON;
    const invalidEmailErrors = errors ? errors.filter((error: any) => error.code === INVALID_EMAIL) : [];

    if (invalidEmailErrors.length > 0) {
      dispatchGiftFlow({ type: UPDATE_SERVER_ERRORS, payload: invalidEmailErrors });
      pushHistory('/gift/customize', history, languageRegionCode);
    }

    return err;
  };

  const handlePaymentSubmit = (creditCardInfo: any) => {
    const {
      plan,
      recaptcha,
      paypal_token,
      apple_pay_token,
      user: { cc_number, cc_csc, cc_expiration_month, cc_expiration_year, zip, billing_first_name, billing_last_name }
    } = creditCardInfo;

    const { recipientName, recipientEmail, message, sendDate, variant, sendMethod, senderEmail, senderName } = giftCustomizeParams;

    const timezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const data = {
      billing_country: billingCountry,
      credit_card_info: { cc_number, cc_csc, cc_expiration_month, cc_expiration_year, zip, billing_first_name, billing_last_name },
      plan,
      paypal_token,
      apple_pay_token,
      background_id: variant,
      message,
      sender_info: { name: senderName, email: senderEmail },
      recipient_info: { name: recipientName },
      currency: currencyCode,
      recaptcha,
      timezone_id: timezoneId
    };

    if (sendMethod === 'email') {
      Object.assign(data, { recipient_info: { ...data.recipient_info, email: recipientEmail }, delivery_date: sendDate });
    }

    const headers = languageRegionCode ? { 'X-Language-Locale': languageRegionCode } : {};

    return serverAjaxPromise({
      type: 'POST',
      contentType: 'application/json',
      headers,
      url: '/api/alltrails/gift',
      data: JSON.stringify(data),
      resReducer: (resp: any) => resp[0]
    })
      .then(handlePaymentSuccess)
      .catch(handlePaymentError);
  };

  return (
    <>
      <Switch>
        <Route path="*/gift" exact>
          <GiftLandingPage currencyCode={currencyCode} plan={plan} />
        </Route>
        <Route path="*/gift/customize" exact>
          <CustomizeGiftForm />
        </Route>
        <Route path="*/login" exact>
          {user ? (
            <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
          ) : (
            <LoginSignupLayout>
              <LoginForm handleLoginSuccess={handleAuthSuccess} useReactRouter />
            </LoginSignupLayout>
          )}
        </Route>
        <Route path="*/signup" exact>
          {user ? (
            <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
          ) : (
            <LoginSignupLayout>
              <SignUpForm handleRegistrationSuccess={handleAuthSuccess} useReactRouter />
            </LoginSignupLayout>
          )}
        </Route>
        <Route path="*/gift/payment" exact>
          {hasFormErrors(giftCustomizeParams) ? (
            <Redirect to={wrapUrlSafe('/gift/customize', languageRegionCode)} />
          ) : (
            <div className={styles.formContainer}>
              {giftCustomizeParams.sendMethod === 'email' && (
                <GiftConfirmation
                  recipientName={giftCustomizeParams.recipientName}
                  recipientEmail={giftCustomizeParams.recipientEmail}
                  sendDate={giftCustomizeParams.sendDate}
                />
              )}
              <PaymentForm
                plan={plan}
                paymentTypes={paymentTypes}
                currencyCode={currencyCode}
                handlePayment={handlePaymentSubmit}
                webProPurchaseLocation={WebProPurchaseLocation.GiftPage}
                hideFinePrint
                showPayWithCard
                setCurrencyCode={setCurrencyCode}
                billingCountry={billingCountry}
                setBillingCountry={setBillingCountry}
              />
            </div>
          )}
        </Route>
        <Route path="*/gift/review" exact>
          {!purchasedGiftCard ? (
            <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
          ) : (
            <GiftReview {...purchasedGiftCard} shopifyProducts={shopifyProducts} showShopifyPreview={countryId === 313} />
          )}
        </Route>
        <Route path="*/gift/preview" exact>
          {!purchasedGiftCard ? <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} /> : <GiftCardPrint giftSubscription={purchasedGiftCard} />}
        </Route>
        <Route path="*/gift*" exact>
          <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
        </Route>
      </Switch>
      {localizedRouteIncludes(location.pathname, MINI_FOOTER_PATHNAMES, languageRegionCode) && <MiniFooter />}
    </>
  );
};

GiftFlowRouting.defaultProps = {
  shopifyProducts: []
};

export default GiftFlowRouting;
